/**
 * Availability manager — the therapist's own intake/exit slot pool (AD-04).
 *
 * A therapist publishes concrete slots; clients claim them. There is no
 * recurrence model by design (see docs/aimhi-direct §4.5), so this screen is
 * "add slots for a day" plus a list of what is published.
 *
 * Two decisions surface directly in the UI:
 *   D-17 — every slot must name the service group(s) it is offered to, so
 *          pilot capacity cannot be silently consumed by other work.
 *   D-03 — slots are typed intake or exit, so end-of-pilot capacity can be
 *          reserved from the start rather than discovered missing in week 5.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Button, TextField, MenuItem,
  Chip, Alert, CircularProgress, Divider, IconButton, Tooltip,
  ToggleButton, ToggleButtonGroup, FormHelperText, Checkbox, ListItemText
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Event as EventIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import api from '../../services/api';
import { publishSlots, getMySlots, cancelMySlot } from '../../services/availabilityApi';

const DURATIONS = [30, 45, 60, 90];

/** Selectable start times, 07:00–20:30 in half hours. */
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 7; h <= 20; h += 1) {
    for (const m of [0, 30]) out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
  return out;
})();

/** Combine a yyyy-mm-dd date and an HH:mm time as local wall-clock. */
const localDateTime = (day, time) => new Date(`${day}T${time}`);

const fmt = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit'
  });

const STATUS_COLOR = { open: 'success', booked: 'primary', cancelled: 'default' };

export default function AvailabilityManager({ token }) {
  const [groups, setGroups] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Draft slot being added
  const [date, setDate] = useState('');
  const [times, setTimes] = useState([]);
  const [duration, setDuration] = useState(30);
  const [slotType, setSlotType] = useState('intake');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [filter, setFilter] = useState('upcoming');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [g, s] = await Promise.all([
        api.get('/service-groups/my-groups?role=therapist', token),
        getMySlots({}, token)
      ]);
      // GET /service-groups/my-groups responds { success, count, data: [...] }.
      const payload = g?.data;
      const myGroups = Array.isArray(payload) ? payload
        : Array.isArray(payload?.data) ? payload.data
        : Array.isArray(payload?.serviceGroups) ? payload.serviceGroups
        : [];
      const normalised = myGroups.map(x => ({
        _id: String(x._id || x.groupId?._id || x.groupId || ''),
        name: x.name || x.groupId?.name || 'Unnamed group'
      })).filter(x => x._id && x._id !== 'undefined');
      setGroups(normalised);
      // Publishing into a single group is the common case — preselect it so a
      // slot can never be created with no allocation by omission.
      if (normalised.length === 1) setSelectedGroups([normalised[0]._id]);
      setSlots(s?.slots || []);
    } catch (err) {
      console.error('Error loading availability:', err);
      setError(err.response?.data?.msg || 'Could not load your availability');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggleGroup = (id) =>
    setSelectedGroups(prev => (prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]));

  const handlePublish = async () => {
    setError('');
    setNotice('');
    if (!date) return setError('Choose a date');
    if (!times.length) return setError('Choose at least one start time');
    if (!selectedGroups.length) return setError('Choose at least one service group to offer this slot to');

    const now = new Date();
    const chosen = times.map(t => localDateTime(date, t)).sort((a, b) => a - b);
    if (chosen.some(d => isNaN(d.getTime()))) return setError('That date and time could not be read');
    const past = chosen.filter(d => d <= now);
    if (past.length) {
      return setError(
        past.length === chosen.length
          ? 'Those times are in the past'
          : `${past.length} of the times chosen are in the past. Remove them, or pick a later date.`
      );
    }

    try {
      setSaving(true);
      const payload = chosen.map(start => ({
        startsAt: start.toISOString(),
        endsAt: new Date(start.getTime() + duration * 60000).toISOString(),
        slotType,
        serviceGroupIds: selectedGroups
      }));
      await publishSlots(payload, token);
      setNotice(
        payload.length === 1
          ? `${slotType === 'intake' ? 'Intake' : 'Exit'} slot published for ${fmt(payload[0].startsAt)}`
          : `${payload.length} ${slotType} slots published`
      );
      setTimes([]);
      await load();
    } catch (err) {
      console.error('Error publishing slots:', err);
      setError(err.response?.data?.msg || 'Could not publish the slots');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (slot) => {
    setError('');
    setNotice('');
    try {
      await cancelMySlot(slot._id, token);
      setNotice('Slot withdrawn');
      await load();
    } catch (err) {
      console.error('Error cancelling slot:', err);
      setError(err.response?.data?.msg || 'Could not withdraw that slot');
    }
  };

  const visible = useMemo(() => {
    const now = Date.now();
    const q = search.trim().toLowerCase();
    return (slots || [])
      .filter(s => (filter === 'upcoming' ? new Date(s.startsAt).getTime() > now : true))
      .filter(s => (filter === 'upcoming' ? s.status !== 'cancelled' : true))
      .filter(s => (typeFilter === 'all' ? true : s.slotType === typeFilter))
      .filter(s => (statusFilter === 'all' ? true : s.status === statusFilter))
      .filter(s => {
        if (!q) return true;
        const client = s.bookedBy
          ? [s.bookedBy.firstName, s.bookedBy.lastName, s.bookedBy.email].filter(Boolean).join(' ')
          : '';
        const groups = (s.serviceGroupIds || []).map(g => g.name || '').join(' ');
        return `${client} ${groups}`.toLowerCase().includes(q);
      });
  }, [slots, filter, typeFilter, statusFilter, search]);

  const counts = useMemo(() => {
    const now = Date.now();
    const future = (slots || []).filter(s => new Date(s.startsAt).getTime() > now && s.status === 'open');
    return {
      intake: future.filter(s => s.slotType === 'intake').length,
      exit: future.filter(s => s.slotType === 'exit').length
    };
  }, [slots]);

  if (loading) {
    return <Card><CardContent><CircularProgress size={22} /></CardContent></Card>;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <EventIcon color="primary" />
          <Typography variant="h6">Availability</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Publish the slots clients can book. Each slot is offered to the service groups you choose,
          so you can give some availability to a programme and keep the rest for your own practice.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

        {!groups.length && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are not an active therapist in any service group, so there is nowhere to publish a slot.
            An administrator needs to add you to one first.
          </Alert>
        )}

        {/* Open future capacity, by type. Exit capacity is easy to forget until
            the first cohort reaches its end (risk R-08), so show it always. */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip size="small" label={`${counts.intake} open intake`} color={counts.intake ? 'success' : 'default'} variant="outlined" />
          <Chip size="small" label={`${counts.exit} open exit`} color={counts.exit ? 'success' : 'default'} variant="outlined" />
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().slice(0, 10) }}
              sx={{ minWidth: 190 }}
              disabled={saving || !groups.length}
            />
            <TextField
              select
              label="Duration"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              sx={{ width: 140 }}
              disabled={saving || !groups.length}
            >
              {DURATIONS.map(d => <MenuItem key={d} value={d}>{d} minutes</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Start times"
              value={times}
              onChange={(e) => setTimes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (selected || []).join(', ')
              }}
              helperText="Pick one or more — a slot is created for each"
              sx={{ minWidth: 240 }}
              disabled={saving || !groups.length || !date}
            >
              {TIME_OPTIONS.map(t => (
                <MenuItem key={t} value={t}>
                  <Checkbox size="small" checked={times.indexOf(t) > -1} />
                  <ListItemText primary={t} />
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={slotType}
                onChange={(_, v) => v && setSlotType(v)}
                disabled={saving || !groups.length}
              >
                <ToggleButton value="intake">Intake</ToggleButton>
                <ToggleButton value="exit">Exit</ToggleButton>
              </ToggleButtonGroup>
              <FormHelperText>
                {slotType === 'intake' ? 'First appointment for a new client' : 'End-of-programme appointment'}
              </FormHelperText>
            </Box>
          </Stack>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Offer this slot to</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {groups.map(g => (
                <Chip
                  key={g._id}
                  label={g.name}
                  onClick={() => toggleGroup(g._id)}
                  color={selectedGroups.includes(g._id) ? 'primary' : 'default'}
                  variant={selectedGroups.includes(g._id) ? 'filled' : 'outlined'}
                  disabled={saving}
                />
              ))}
            </Stack>
            <FormHelperText>
              A slot with no service group is offered to nobody. Only clients in the groups you pick will see it.
            </FormHelperText>
          </Box>

          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handlePublish}
              disabled={saving || !groups.length || !date || !times.length}
            >
              {saving ? 'Publishing…' : times.length > 1 ? `Publish ${times.length} slots` : 'Publish slot'}
            </Button>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1">
            Published slots
            {visible.length !== (slots || []).length && (
              <Typography component="span" variant="body2" color="text.secondary">
                {' '}({visible.length} of {(slots || []).length})
              </Typography>
            )}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filter}
            onChange={(_, v) => v && setFilter(v)}
          >
            <ToggleButton value="upcoming">Upcoming</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Search"
            placeholder="Client or service group"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />,
              endAdornment: search ? (
                <IconButton size="small" onClick={() => setSearch('')} aria-label="clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null
            }}
          />
          <ToggleButtonGroup exclusive size="small" value={typeFilter} onChange={(_, v) => v && setTypeFilter(v)}>
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="intake">Intake</ToggleButton>
            <ToggleButton value="exit">Exit</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup exclusive size="small" value={statusFilter} onChange={(_, v) => v && setStatusFilter(v)}>
            <ToggleButton value="all">Any</ToggleButton>
            <ToggleButton value="open">Free</ToggleButton>
            <ToggleButton value="booked">Booked</ToggleButton>
          </ToggleButtonGroup>
          {(search || typeFilter !== 'all' || statusFilter !== 'all') && (
            <Button size="small" onClick={() => { setSearch(''); setTypeFilter('all'); setStatusFilter('all'); }}>
              Clear
            </Button>
          )}
        </Stack>

        {!visible.length && (
          <Typography variant="body2" color="text.secondary">
            No slots {filter === 'upcoming' ? 'coming up' : 'yet'}. Publish one above so clients can book.
          </Typography>
        )}

        <Stack spacing={1}>
          {visible.map(s => (
            <Stack
              key={s._id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, flexWrap: 'wrap' }}
            >
              <Typography variant="body2" sx={{ minWidth: 190 }}>{fmt(s.startsAt)}</Typography>
              <Chip size="small" label={s.slotType} variant="outlined" />
              <Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || 'default'} />
              {(s.serviceGroupIds || []).map(g => (
                <Chip key={g._id || g} size="small" variant="outlined" label={g.name || 'group'} />
              ))}
              {s.status === 'booked' && s.bookedBy && (
                <Typography variant="body2" color="text.secondary">
                  {[s.bookedBy.firstName, s.bookedBy.lastName].filter(Boolean).join(' ') || s.bookedBy.email}
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {s.status === 'open' && (
                <Tooltip title="Withdraw this slot">
                  <IconButton size="small" onClick={() => handleCancel(s)} aria-label="withdraw slot">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {s.status === 'booked' && (
                <Tooltip title="Booked slots are cancelled through the appointment, so the client is notified">
                  <span>
                    <IconButton size="small" disabled aria-label="booked"><DeleteIcon fontSize="small" /></IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
